import React, { useState } from 'react'
import { Button, Table } from 'antd'
import { TableOutlined, LineChartOutlined, DownloadOutlined } from '@ant-design/icons'
import CHART_COLORS from '../../util/chartPalette'
import { formatDateTimeET, formatDateET } from '../../util/datetime'

// Shared "Data View" affordance for the TracPatch D3 charts.
//
// Every chart already derives a per-day array before drawing; this module
// gives them a consistent way to (a) render the dark DATA VIEW / CHART toggle
// that sits at the right edge of the chart's control row, and (b) spill that
// same per-day array into a plain Date / Value grid. Days without a reading
// are simply omitted by the caller (it passes already-filtered rows), so the
// grid only lists dates that actually have data.

// Dark navy pill matching the mockup. Flips label + icon between the two views.
// Sized by `.chartControls .chart-data-view` in patient.scss, where the whole
// control row is kept compact enough to stay on ONE line in a half-width card.
export const DataViewToggle = ({ active, onClick, style }) => (
  <Button
    size='small'
    className='chart-data-view'
    onClick={onClick}
    icon={active ? <LineChartOutlined /> : <TableOutlined />}
    style={{
      background: CHART_COLORS.navy,
      color: '#fff',
      borderColor: CHART_COLORS.navy,
      fontWeight: 700,
      ...style,
    }}
  >
    {active ? 'CHART' : 'DATA VIEW'}
  </Button>
)

// Six rows is the page a chart card can show without growing; a larger page
// scrolls inside the table instead, so the pager and the download button stay
// in reach.
const PAGE_SIZE = 6
const PAGE_SIZE_OPTIONS = ['6', '12', '20', '50', '100']
const ROW_HEIGHT = 39 // antd size='small' bordered row

const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v)
  return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

// The whole grid as CSV -- every row, not the current page -- in chart order
// (oldest first, as the x axis reads). Each cell is what the grid shows: the
// Date column's own formatting, and a value column's render when it yields
// text; otherwise the raw value.
export const gridToCsv = (rows, columns) => {
  const header = columns.map((c) => csvCell(c.title)).join(',')
  const lines = rows.map((r) =>
    columns
      .map((c) => {
        const raw = r[c.dataIndex]
        const out = c.render ? c.render(raw, r) : raw
        return csvCell(typeof out === 'string' || typeof out === 'number' ? out : raw)
      })
      .join(',')
  )
  return [header, ...lines].join('\r\n')
}

export const downloadCsv = (text, filename) => {
  // The BOM makes Excel read the file as UTF-8 (degree signs, en dashes).
  const blob = new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

// "2026-09-26" from a row date, in the Eastern day the grid shows.
const isoDay = (d) => {
  const m = formatDateET(d).match(/^(\d+)\/(\d+)\/(\d+)$/)
  return m ? `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}` : ''
}

// Renders rows as a Date + value(s) grid.
//   rows         : [{ date: Date|string|number, value, ...extra }] — pre-filtered
//                  to the days with data. The `date` should be the actual
//                  timestamp of the day's (last) reading; it is shown as a US
//                  Eastern date AND time, e.g. "6/12/2026 3:45PM".
//   valueColumns : optional column defs for multi-series charts. Defaults to a
//                  single column driven by `valueLabel` reading `value`.
//   valueLabel   : header for the default single value column.
//   dateOnly     : when true, render the Date column as date-only (no time) —
//                  for series whose rows have no meaningful time of day.
//   dateTitle    : header for the Date column (defaults to 'Date').
//   csvName      : file-name stem for the download; the rows' date span is
//                  appended, e.g. "tracpatch-steps_2026-09-01_2026-09-30.csv".
//
// Rows default to 6 per page and most-recent-first (the Date column is
// sortable, defaulting to descending). The pager's size picker is always on;
// a page larger than 6 scrolls inside the table. "Download CSV" exports every
// row the grid holds -- the same values the chart draws for the current range
// (hourly in a 1-Day view, one per day in 7 / 14 / 30).
export const ChartDataGrid = ({
  rows,
  valueLabel = 'Value',
  valueColumns,
  dateOnly = false,
  dateTitle = 'Date',
  csvName = 'chart-data',
}) => {
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const dateCol = {
    title: dateTitle,
    dataIndex: 'date',
    key: 'date',
    defaultSortOrder: 'descend',
    sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    render: (d) => (d == null ? '' : dateOnly ? formatDateET(d) : formatDateTimeET(d)),
  }
  const cols = valueColumns || [{ title: valueLabel, dataIndex: 'value', key: 'value' }]
  const columns = [dateCol, ...cols]
  const dataSource = (rows || []).map((r, i) => ({ key: i, ...r }))

  const handleDownload = () => {
    const ordered = dataSource.slice().sort(dateCol.sorter)
    const first = isoDay(ordered[0].date)
    const last = isoDay(ordered[ordered.length - 1].date)
    const span = first && last ? (first === last ? first : `${first}_${last}`) : ''
    downloadCsv(gridToCsv(ordered, columns), `${csvName}${span ? '_' + span : ''}.csv`)
  }

  return (
    <div style={{ padding: 10, margin: 15 }}>
      <Table
        size='small'
        bordered
        columns={columns}
        dataSource={dataSource}
        scroll={pageSize > PAGE_SIZE ? { y: PAGE_SIZE * ROW_HEIGHT } : undefined}
        pagination={{
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onShowSizeChange: (_, size) => setPageSize(size),
          hideOnSinglePage: true,
          size: 'small',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
        <span style={{ fontSize: 12, color: '#627184' }}>
          {dataSource.length} row{dataSource.length === 1 ? '' : 's'}
        </span>
        <Button
          size='small'
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          disabled={dataSource.length === 0}
        >
          Download CSV
        </Button>
      </div>
    </div>
  )
}
